import { BehaviorData } from "../behaviordata";

/** Type identifier for Model components */
export const MODEL_TYPE = "Model" as const;

/**
 * Controls 3D model loading and display
 */
export interface ModelData extends BehaviorData {
    "@type": typeof MODEL_TYPE;

    /**
     * How the model meets a layout slot whose proportions differ from its
     * own — `object-fit` for a mesh, in the same vocabulary Image and Video
     * use for a media quad.
     *
     * `none` (the default) is what every model has always done: the node's
     * scale is the author's sizing handle, layout measures the geometry
     * through it and never writes it. A model dropped into a slot keeps its
     * real-world size, which is the only sane default for a piece of set
     * dressing placed in metres.
     *
     * `contain` hands the scale to layout: the model is scaled uniformly,
     * as large as fits inside the slot, never distorted. This is what a
     * template means when it ships its own geometry for a hole it authored
     * — a phone body drawn into a card-sized box.
     *
     * `scale-down` is `contain` that never enlarges: a model larger than
     * its slot shrinks to fit, one that already fits keeps its size.
     *
     * `fill` scales per axis to the slot, distorting. A mesh with sculpted
     * corners or internal parts smears under it; `MorphStretch` is the
     * right tool where the asset authors stretch morphs.
     *
     * `cover` would need to clip against the slot, which the engine cannot
     * do for a mesh, so it is accepted and clamped to `contain` with a
     * warning rather than silently overflowing.
     *
     * Only slot-determined axes constrain the fit: an axis the container
     * Hugs was sized BY this model, and a flat depth axis is the 2D-canvas
     * convention rather than an instruction to squash the geometry.
     * @default "none"
     */
    fit?: 'fill' | 'contain' | 'scale-down' | 'cover' | 'none';

    /**
     * Where a FITTED model sits inside its slot on the vertical axis.
     *
     * `center` (the default) places the geometry's bounds centre at the
     * slot centre — a hero floating in a card. `bottom` seats the bounds
     * on the slot's floor, which is what a slot standing on a plinth or a
     * bench means: a low, wide model (a car) contain-fits by its width and
     * would otherwise hover a hand's width above the surface it was placed
     * on. Read only under a fitting `fit`; `none` keeps the author's
     * transform whole.
     * @default "center"
     */
    fitAnchor?: 'center' | 'bottom';
}
